import type {
  IgnoredExitReason,
  IgnoredExitRecord,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/IgnoredExit';

export enum conflictResolveType {
  conflict = 'conflict',
  localPreference = 'localPreference',
  remotePreference = 'remotePreference',
  keepBoth = 'keepBoth',
  ignoredExitDeleteConfirmed = 'ignoredExitDeleteConfirmed',
}

export enum ConflictDataType {
  normal = 'normal',
  ignoredExitDeleteConfirm = 'ignoredExitDeleteConfirm',
}

export type ConflictFileInfo = {
  path: string;
  size: number;
  mTime: string;
};

export type IgnoredExitConflictInfo = {
  id: string;
  side: IgnoredExitRecord['side'];
  reason?: IgnoredExitReason;
  sourcePath: string;
  ignoredPath: string;
  isDir: boolean;
};

export type ConflictDataObj = {
  type?: ConflictDataType;
  result: conflictResolveType;
  autoChoose: boolean;
  localItem: ConflictFileInfo;
  remoteItem: ConflictFileInfo;
  ignoredExit?: IgnoredExitConflictInfo;
};

export default class ConflictData {
  private type: ConflictDataType = ConflictDataType.normal;
  private result: conflictResolveType = conflictResolveType.conflict;
  private autoChoose = false;
  public localItem!: ConflictFileInfo;
  public remoteItem!: ConflictFileInfo;
  public ignoredExit?: IgnoredExitConflictInfo;

  public toDataObj(): ConflictDataObj {
    return {
      type: this.type,
      result: this.result,
      autoChoose: this.autoChoose,
      localItem: this.localItem,
      remoteItem: this.remoteItem,
      ignoredExit: this.ignoredExit,
    };
  }

  public fromDataObj(data: ConflictDataObj) {
    this.type = data.type ?? ConflictDataType.normal;
    this.result = data.result;
    this.autoChoose = data.autoChoose;
    this.localItem = data.localItem;
    this.remoteItem = data.remoteItem;
    this.ignoredExit = data.ignoredExit;
  }

  public chooseLocalPreference(autoChoose = false) {
    this.result = conflictResolveType.localPreference;
    this.autoChoose = autoChoose;
  }

  public chooseRemotePreference(autoChoose = false) {
    this.result = conflictResolveType.remotePreference;
    this.autoChoose = autoChoose;
  }

  public chooseKeepBoth(autoChoose = false) {
    this.result = conflictResolveType.keepBoth;
    this.autoChoose = autoChoose;
  }

  public chooseIgnoredExitDeleteConfirmed() {
    this.result = conflictResolveType.ignoredExitDeleteConfirmed;
    this.autoChoose = false;
  }

  public setupConflictData(localItem: ConflictFileInfo, remoteItem: ConflictFileInfo) {
    this.type = ConflictDataType.normal;
    this.result = conflictResolveType.conflict;
    this.autoChoose = false;
    this.localItem = localItem;
    this.remoteItem = remoteItem;
    this.ignoredExit = undefined;
  }

  public setupIgnoredExitDeleteConfirm(record: IgnoredExitRecord, size: number, mTime: string) {
    this.type = ConflictDataType.ignoredExitDeleteConfirm;
    this.result = conflictResolveType.conflict;
    this.autoChoose = false;
    this.ignoredExit = {
      id: record.id,
      side: record.side,
      reason: record.reason,
      sourcePath: record.sourcePath,
      ignoredPath: record.ignoredPath,
      isDir: record.isDir,
    };
    this.localItem = {
      path: record.side === 'local' ? record.ignoredPath : record.sourcePath,
      size,
      mTime,
    };
    this.remoteItem = {
      path: record.side === 'remote' ? record.ignoredPath : record.sourcePath,
      size,
      mTime,
    };
  }

  public isCurrentConflict(localPath: string, remotePath: string): boolean {
    return (
      localPath === this.localItem.path &&
      remotePath === this.remoteItem.path &&
      this.result !== conflictResolveType.conflict
    );
  }

  public getResolveResult(): conflictResolveType {
    return this.result;
  }
}
